import {
  type AskPunttMessage,
  type AskPunttSearchProgress,
} from "@mg/schemas/src/christo/askPuntt";
import { useCallback, useEffect, useRef, useState } from "react";

export type StreamPhase =
  | "idle"
  | "searching"
  | "content"
  | "structured"
  | "done";

export interface StreamState {
  phase: StreamPhase;
  /** Search sources revealed so far, with current statuses */
  searchProgress: AskPunttSearchProgress[];
  /** Content text streamed so far (word by word) */
  streamedContent: string;
  /** Whether to show the structured content */
  showStructured: boolean;
  /** How many structured sections to reveal (0=risk, 1=+shortAnswer, 2=+alternatives, 3=+analysis, 4=+timeSaved) */
  structuredSections: number;
  /** Whether streaming is complete */
  isComplete: boolean;
}

const INITIAL_STATE: StreamState = {
  phase: "idle",
  searchProgress: [],
  streamedContent: "",
  showStructured: false,
  structuredSections: 0,
  isComplete: false,
};

export interface StreamSimulationOptions {
  /** Skip the search-source replay phase (user already saw results via polling) */
  skipSearchPhase?: boolean;
}

/**
 * Hook that simulates LLM-style streaming for a complete message.
 * Phases:
 *   1. Search sources appear one-by-one (searching → done)  — skippable
 *   2. Content text streams in word-by-word
 *   3. Structured content sections reveal progressively
 */
export function useStreamSimulation(
  message: AskPunttMessage | null,
  enabled = true,
  options?: StreamSimulationOptions,
) {
  const [state, setState] = useState<StreamState>(INITIAL_STATE);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const messageIdRef = useRef<string | null>(null);
  const skipSearch = options?.skipSearchPhase ?? false;

  const cleanup = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  }, []);

  useEffect(() => {
    if (!message || !enabled || message.role !== "assistant") {
      return;
    }

    // Prevent re-running for the same message
    if (messageIdRef.current === message._id) return;
    messageIdRef.current = message._id;

    cleanup();

    const searchSources = message.searchProgress ?? [];

    if (skipSearch && searchSources.length > 0) {
      setState({
        ...INITIAL_STATE,
        searchProgress: searchSources,
        phase: "content",
      });
    } else {
      setState(INITIAL_STATE);
    }

    const schedule = (fn: () => void, delay: number) => {
      const t = setTimeout(fn, delay);
      timeoutsRef.current.push(t);
      return t;
    };

    let elapsed = 0;
    const contentWords = message.content ? message.content.split(" ") : [];
    const hasStructured = !!message.structuredContent;

    // ── Phase 1: Search sources (skipped when user already saw them) ──
    if (!skipSearch && searchSources.length > 0) {
      setState((s) => ({ ...s, phase: "searching" }));

      searchSources.forEach((source, idx) => {
        const searchingDelay = elapsed + idx * 800;
        schedule(() => {
          setState((s) => ({
            ...s,
            searchProgress: [
              ...s.searchProgress,
              {
                ...source,
                status: "searching",
                resultCount: undefined,
                results: undefined,
              },
            ],
          }));
        }, searchingDelay);

        const doneDelay = searchingDelay + 600 + Math.random() * 400;
        schedule(() => {
          setState((s) => ({
            ...s,
            searchProgress: s.searchProgress.map((sp, i) =>
              i === idx ? { ...source, status: "done" } : sp,
            ),
          }));
        }, doneDelay);

        elapsed = doneDelay + 200;
      });
    }

    // ── Phase 2: Content text streaming ──────────────────────────
    const contentStart = elapsed + 300;

    if (contentWords.length > 0) {
      schedule(() => {
        setState((s) => ({ ...s, phase: "content" }));
      }, contentStart);

      const wordsPerChunk = 3;
      const chunkDelay = 60;

      for (let i = 0; i < contentWords.length; i += wordsPerChunk) {
        const chunk = contentWords.slice(i, i + wordsPerChunk).join(" ");
        const wordDelay = contentStart + (i / wordsPerChunk) * chunkDelay;
        schedule(() => {
          setState((s) => ({
            ...s,
            streamedContent: s.streamedContent
              ? s.streamedContent + " " + chunk
              : chunk,
          }));
        }, wordDelay);
        elapsed = wordDelay;
      }
    }

    // ── Phase 3: Structured content ──────────────────────────────
    if (hasStructured) {
      const structuredStart = elapsed + 400;

      schedule(() => {
        setState((s) => ({
          ...s,
          phase: "structured",
          showStructured: true,
          structuredSections: 1, // Risk badge + short answer
        }));
      }, structuredStart);

      // Reveal alternatives
      schedule(() => {
        setState((s) => ({ ...s, structuredSections: 2 }));
      }, structuredStart + 800);

      // Reveal regulatory analysis
      schedule(() => {
        setState((s) => ({ ...s, structuredSections: 3 }));
      }, structuredStart + 1600);

      // Reveal time saved
      schedule(() => {
        setState((s) => ({ ...s, structuredSections: 4 }));
      }, structuredStart + 2400);

      // Done
      schedule(() => {
        setState((s) => ({
          ...s,
          phase: "done",
          isComplete: true,
        }));
      }, structuredStart + 3000);
    } else {
      // No structured content — done after text
      schedule(() => {
        setState((s) => ({
          ...s,
          phase: "done",
          isComplete: true,
        }));
      }, elapsed + 400);
    }

    return cleanup;
  }, [message, enabled, skipSearch, cleanup]);

  return state;
}
